// Réglages › Dossiers › Types d'événement : les types d'audience et de rendez-vous
// du cabinet, avec leur couleur dans l'agenda.
import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { ColorSwatchPicker } from '../ui/ColorSwatchPicker';
import { FaCheck, FaPen, FaPlus, FaTrashCan, FaXmark } from '../ui/FaIcons';
import {
  useCreateEventType,
  useDeleteEventType,
  useEventTypesWithUsage,
  useUpdateEventType,
} from '../../hooks/queries/useEventTypes';
import {
  EVENT_FAMILY_LABEL,
  type EventTypeFamily,
  type EventTypeWithUsage,
} from '../../services/calendar/eventTypeService';
import { EVENT_COLOR_PALETTE } from '../../services/calendar/eventColors';

const FAMILIES: EventTypeFamily[] = ['hearing', 'appointment'];
const USAGE_NOUN: Record<EventTypeFamily, string> = { hearing: 'audience', appointment: 'rendez-vous' };
const PLACEHOLDER: Record<EventTypeFamily, string> = {
  hearing: 'Référé, conciliation…',
  appointment: 'Déplacement, réunion…',
};
const ADD_LABEL: Record<EventTypeFamily, string> = {
  hearing: 'Ajouter un type d’audience',
  appointment: 'Ajouter un type de rendez-vous',
};

function fail(err: unknown) {
  toast.error(err instanceof Error ? err.message : 'Action impossible');
}

export function EventTypesSection() {
  const { data: types = [], isLoading } = useEventTypesWithUsage();

  return (
    <section className="rounded-cime border border-neutral-200 bg-white p-4 space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-cime-text-primary">Types d’événement</h2>
        <p className="text-sm text-cime-text-muted mt-1">
          Les types proposés pour une audience ou un rendez-vous, et leur couleur dans l’agenda.
          Chaque personne peut remplacer une couleur pour elle seule, dans son profil.
        </p>
      </div>
      {isLoading ? (
        <p className="text-sm text-cime-text-muted">Chargement…</p>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2 xl:items-start">
          {FAMILIES.map((family) => (
            <FamilyBlock key={family} family={family} types={types.filter((t) => t.family === family)} />
          ))}
        </div>
      )}
    </section>
  );
}

function FamilyBlock({ family, types }: { family: EventTypeFamily; types: EventTypeWithUsage[] }) {
  const create = useCreateEventType();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  // Par défaut, la première couleur de la palette que la famille n'utilise pas encore.
  const used = new Set(types.map((t) => t.color.toLowerCase()));
  const color = picked ?? EVENT_COLOR_PALETTE.find((c) => !used.has(c)) ?? EVENT_COLOR_PALETTE[0];

  function close() {
    setAdding(false);
    setName('');
    setPicked(null);
  }

  function add(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // Le formulaire reste ouvert après un ajout : on enchaîne souvent plusieurs types.
    create.mutate(
      { family, name, color },
      {
        onSuccess: () => {
          setName('');
          setPicked(null);
        },
        onError: fail,
      },
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-200 bg-[#fbfaf7] px-4 py-2.5">
        <h3 className="text-[13px] font-semibold text-cime-text-primary">
          {EVENT_FAMILY_LABEL[family]}
          <span className="ml-2 text-xs font-normal tabular-nums text-cime-text-muted">
            {types.length} type{types.length > 1 ? 's' : ''}
          </span>
        </h3>
      </div>
      <ul className="m-0 list-none divide-y divide-neutral-100 p-0">
        {types.map((type) => (
          <EventTypeRow key={type.id} type={type} />
        ))}
        <li className="px-4 py-2.5">
          {adding ? (
            <form onSubmit={add} className="flex flex-wrap items-center gap-3">
              <ColorSwatchPicker value={color} onChange={setPicked} label="Couleur du nouveau type" />
              <div className="min-w-[220px] flex-1">
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') close();
                  }}
                  placeholder={PLACEHOLDER[family]}
                  className="h-9"
                />
              </div>
              <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
                Ajouter
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={close}>
                Terminer
              </Button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex h-9 items-center gap-2 rounded-md px-1 text-[13px] font-medium text-cime-primary hover:underline"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full border border-dashed border-cime-primary/40">
                <FaPlus className="h-3 w-3" aria-hidden />
              </span>
              {ADD_LABEL[family]}
            </button>
          )}
        </li>
      </ul>
    </div>
  );
}

function EventTypeRow({ type }: { type: EventTypeWithUsage }) {
  const update = useUpdateEventType();
  const remove = useDeleteEventType();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(type.name);
  const noun = USAGE_NOUN[type.family as EventTypeFamily];
  const plural = type.family === 'hearing' && type.usage > 1 ? 's' : '';

  function rename() {
    const next = name.trim();
    if (!next || next === type.name) {
      setEditing(false);
      setName(type.name);
      return;
    }
    update.mutate({ id: type.id, patch: { name: next } }, { onSuccess: () => setEditing(false), onError: fail });
  }

  return (
    <li className={cn('flex flex-wrap items-center gap-3 px-4 py-2.5', !type.is_active && 'bg-neutral-50')}>
      <ColorSwatchPicker
        value={type.color}
        onChange={(color) => update.mutate({ id: type.id, patch: { color } }, { onError: fail })}
        label={`Couleur de ${type.name}`}
      />
      {editing ? (
        <form
          className="flex min-w-[200px] flex-1 items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            rename();
          }}
        >
          <div className="min-w-0 flex-1">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          </div>
          <IconButton label="Enregistrer le nom" type="submit"><FaCheck className="h-3.5 w-3.5" /></IconButton>
          <IconButton label="Annuler" onClick={() => { setEditing(false); setName(type.name); }}><FaXmark className="h-3.5 w-3.5" /></IconButton>
        </form>
      ) : (
        <span className={cn('min-w-[160px] flex-1 truncate text-sm font-medium', type.is_active ? 'text-cime-text-primary' : 'text-neutral-400')}>
          {type.name}
          {!type.is_active ? <span className="ml-2 text-[11px] font-normal">désactivé</span> : null}
        </span>
      )}
      <span className="text-right text-xs tabular-nums text-neutral-400">
        {type.usage} {noun}{plural}
      </span>
      <div className="flex items-center gap-1">
        {!editing ? <IconButton label="Renommer" onClick={() => setEditing(true)}><FaPen className="h-3.5 w-3.5" /></IconButton> : null}
        <button
          type="button"
          onClick={() => update.mutate({ id: type.id, patch: { is_active: !type.is_active } }, { onError: fail })}
          className="h-8 rounded-md px-2 text-[13px] font-medium text-cime-text-muted transition-colors hover:bg-neutral-100 hover:text-cime-primary"
        >
          {type.is_active ? 'Désactiver' : 'Réactiver'}
        </button>
        <IconButton
          label={type.usage > 0 ? 'Suppression impossible : ce type est utilisé. Désactivez-le.' : 'Supprimer'}
          disabled={type.usage > 0 || remove.isPending}
          danger
          onClick={() => remove.mutate(type.id, { onError: fail })}
        >
          <FaTrashCan className="h-3.5 w-3.5" />
        </IconButton>
      </div>
    </li>
  );
}

function IconButton({
  label, children, onClick, type = 'button', disabled, danger,
}: {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-md text-neutral-400 transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        danger ? 'hover:bg-red-50 hover:text-red-600 disabled:hover:bg-transparent disabled:hover:text-neutral-400' : 'hover:bg-neutral-100 hover:text-cime-primary',
      )}
    >
      {children}
    </button>
  );
}
